import { useState } from "react";
import { videoReviews, VideoReview } from "@/mocks/videos";

const getYoutubeEmbedUrl = (url: string): string => {
  try {
    if (url.includes("youtu.be/")) {
      const id = url.split("youtu.be/")[1].split("?")[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("youtube.com/watch")) {
      const u = new URL(url);
      const id = u.searchParams.get("v");
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("youtube.com/embed/")) {
      return url;
    }
  } catch {
    // ignore
  }
  return url;
};

const getYoutubeThumbnail = (url: string): string => {
  try {
    let id = "";
    if (url.includes("youtu.be/")) {
      id = url.split("youtu.be/")[1].split("?")[0];
    } else if (url.includes("youtube.com/watch")) {
      const u = new URL(url);
      id = u.searchParams.get("v") || "";
    } else if (url.includes("youtube.com/embed/")) {
      id = url.split("youtube.com/embed/")[1].split("?")[0];
    }
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  } catch {
    // ignore
  }
  return "";
};

interface VideoModalProps {
  video: VideoReview;
  onClose: () => void;
}

const VideoModal = ({ video, onClose }: VideoModalProps) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
    onClick={onClose}
  >
    <div
      className="relative bg-black rounded-2xl overflow-hidden w-full max-w-3xl"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
      >
        <i className="ri-close-line text-white text-lg" />
      </button>
      <div className="w-full" style={{ aspectRatio: "16/9" }}>
        <iframe
          src={`${getYoutubeEmbedUrl(video.youtubeUrl)}?autoplay=1&rel=0`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
          style={{ border: "none" }}
        />
      </div>
      <div className="p-4 bg-stone-900">
        <p className="text-sm font-bold text-white mb-0.5">{video.title}</p>
        <p className="text-xs text-stone-400">{video.reviewer} · {video.location} · {video.date}</p>
      </div>
    </div>
  </div>
);

const VideoCard = ({ video, onPlay }: { video: VideoReview; onPlay: () => void }) => {
  const thumb = getYoutubeThumbnail(video.youtubeUrl);

  return (
    <div
      className="group bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      onClick={onPlay}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: "16/9" }}>
        {thumb ? (
          <img
            src={thumb}
            alt={video.title}
            className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-stone-200 flex items-center justify-center">
            <i className="ri-video-line text-stone-400 text-3xl" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <div className="w-12 h-12 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
            <i className="ri-play-fill text-stone-900 text-xl" style={{ marginLeft: "2px" }} />
          </div>
        </div>
        <div className="absolute top-2 left-2">
          <span className="bg-stone-900/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            영상 리뷰
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="text-sm font-bold text-stone-900 mb-1 line-clamp-1">{video.title}</h3>
        <p className="text-xs text-stone-500 mb-2 line-clamp-2 leading-relaxed">{video.desc}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 flex items-center justify-center bg-stone-100 rounded-full">
              <i className="ri-user-line text-stone-500 text-[10px]" />
            </div>
            <span className="text-xs text-stone-500">{video.reviewer}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-stone-400">
            <i className="ri-map-pin-line text-[10px]" />
            {video.location}
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-stone-100 flex items-center gap-1">
          <i className="ri-lock-line text-stone-400 text-[10px]" />
          <span className="text-[10px] text-stone-400">{video.product}</span>
        </div>
      </div>
    </div>
  );
};

const CategoryPreview = () => {
  const [playingVideo, setPlayingVideo] = useState<VideoReview | null>(null);

  return (
    <section className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-10">
          <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-2">Video Reviews</p>
          <h2 className="text-3xl md:text-4xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            영상 리뷰
          </h2>
          <p className="text-sm text-stone-500 mt-2">실제 고객들의 생생한 설치 영상 후기를 확인하세요.</p>
        </div>

        {/* Video Grid */}
        {videoReviews.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-stone-100">
            <div className="w-14 h-14 flex items-center justify-center bg-stone-100 rounded-full mb-4">
              <i className="ri-video-line text-stone-400 text-2xl" />
            </div>
            <p className="text-sm font-bold text-stone-700 mb-1">등록된 영상이 없습니다</p>
            <p className="text-xs text-stone-400">src/mocks/videos.ts 파일에 유튜브 URL을 추가해주세요.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {videoReviews.map((video) => (
              <VideoCard key={video.id} video={video} onPlay={() => setPlayingVideo(video)} />
            ))}
          </div>
        )}
      </div>

      {playingVideo && (
        <VideoModal video={playingVideo} onClose={() => setPlayingVideo(null)} />
      )}
    </section>
  );
};

export default CategoryPreview;
